import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AgentTypeSelector from './AgentTypeSelector';
import SimpleAgentWizard from './SimpleAgentWizard';
import AgentWizard from './AgentWizard';
import PromptAgentWizard from './PromptAgentWizard';

const DeprecationBanner = () => {
  const navigate = useNavigate();
  return (
    <div style={{
      background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
      border: "1px solid #f59e0b",
      borderRadius: 8,
      padding: "12px 20px",
      margin: "0 0 16px 0",
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
    }}>
      <span style={{ fontSize: 20 }}>&#9888;&#65039;</span>
      <div style={{ flex: 1, minWidth: 200 }}>
        <strong style={{ color: "#92400e", fontSize: 14 }}>Este recurso será descontinuado em breve.</strong>
        <p style={{ margin: "4px 0 0", color: "#78350f", fontSize: 13 }}>
          Aconselhamos que você comece a criar seus agentes no <strong>Agentes 2.0</strong>, nossa nova plataforma com mais recursos e melhor experiência.
        </p>
      </div>
      <button
        onClick={() => navigate("/agents-flow")}
        style={{
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          padding: "8px 16px",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Ir para Agentes 2.0
      </button>
    </div>
  );
};

const AgentCreator = ({ imageLibrary = [] }) => {
  const location = useLocation();
  const [creationMode, setCreationMode] = useState('selector'); // 'selector', 'basic', 'advanced', 'prompt'
  const [basicToAdvancedData, setBasicToAdvancedData] = useState(null);
  
  // Verificar se está em modo de edição e determinar o tipo
  const editData = location?.state?.data || location?.state?.editData;
  const isEditing = location?.state?.isEditing;
  const creationType = location?.state?.creationType;
  
  useEffect(() => {
    if (isEditing && editData) {
      // Se há um creationType específico (ex: prompt), usar ele
      if (creationType === 'prompt') {
        setCreationMode('prompt');
      }
      // Senão, verificar o campo simple_agent para determinar o modo
      else if (editData.simple_agent === true) {
        setCreationMode('basic');
      } else {
        setCreationMode('advanced');
      }
    }
  }, [isEditing, editData, creationType]);

  const handleSelectType = useCallback((type) => {
    if (type === 'basic') {
      setCreationMode('basic');
    } else if (type === 'advanced') {
      setCreationMode('advanced');
    } else if (type === 'prompt') {
      setCreationMode('prompt');
    }
    setBasicToAdvancedData(null); // Reset upgrade data
  }, []);

  const handleUpgradeToAdvanced = useCallback((basicData) => {
    // Mapear dados do formulário básico para o formato do AgentWizard avançado
    const advancedData = {
      // Campos básicos (idênticos)
      agentName: basicData.agentName,
      agentJob: basicData.agentJob,
      agentGender: basicData.agentGender,
      agentPersona: basicData.agentPersona,
      agentArea: basicData.agentArea,
      businessInfo: basicData.businessInfo,

      // Campos com valores padrão compatíveis
      agent_image: null,
      language: "default",
      languageStyle: ["friendly", "natural"],
      fundamentalRules: ["noMarkdown", "avoidFormal"],
      prohibitedWords: "",
      preferredWords: "",
      address: "",
      paymentMethods: [],

      // Fluxo padrão compatível com o agente básico
      defaultFlowSteps: [
        {
          id: "step_" + Date.now(),
          key: "greeting",
          label: "Saudação",
          icon: "👋",
          description: "Como o agente vai cumprimentar cada pessoa que chegar",
          placeholder: "Exemplo: Oi! Tudo bem? Eu sou a Maria, como posso te ajudar hoje? 😊",
          order: 0,
        },
      ],

      defaultFlow: {
        greeting: `Oi! Eu sou ${basicData.agentName}. Como posso te ajudar?`,
        presentation: "",
        needIdentification: "",
        direction: "",
        confirmation: "",
        upsell: "",
        availability: "",
        dataCollection: "",
        closing: "",
        greetingNextStep: "",
        presentationNextStep: "",
        needIdentificationNextStep: "",
        directionNextStep: "",
        confirmationNextStep: "",
        upsellNextStep: "",
        availabilityNextStep: "",
        dataCollectionNextStep: "",
        closingNextStep: "",
        customStepNames: {},
      },

      // Sem fluxos especiais inicialmente
      specialFlows: [],

      // Funções desabilitadas inicialmente
      functionsEnabled: false,
      functionSchemas: {},

      // Interações básicas desabilitadas
      interactions: {
        conectores: { enabled: false, value: "" },
        validacao: { enabled: false, value: "" },
        celebracao: { enabled: false, value: "" },
        upsell: { enabled: false, value: "" },
        emojis: { enabled: false, value: "" },
        mantra: { enabled: false, value: "" },
      },

      // Arquivos de treinamento vazios
      trainingFiles: [],
      knowledgeBaseFileIds: [],
    };

    setBasicToAdvancedData(advancedData);
    setCreationMode('advanced');
  }, []);

  const handleBackToSelector = useCallback(() => {
    setCreationMode('selector');
    setBasicToAdvancedData(null);
  }, []);

  const renderContent = () => {
    switch (creationMode) {
      case 'selector':
        return <AgentTypeSelector onSelectType={handleSelectType} />;

      case 'basic':
        return (
          <SimpleAgentWizard
            onUpgradeToAdvanced={handleUpgradeToAdvanced}
            onBack={!isEditing ? handleBackToSelector : null}
            editData={isEditing ? editData : null}
            isEditing={isEditing}
          />
        );

      case 'advanced':
        return (
          <AgentWizard
            imageLibrary={imageLibrary}
            initialData={basicToAdvancedData}
            isUpgradeFromBasic={!!basicToAdvancedData}
            onBack={!isEditing ? handleBackToSelector : null}
          />
        );

      case 'prompt':
        return (
          <PromptAgentWizard
            onBack={handleBackToSelector}
          />
        );

      default:
        return <AgentTypeSelector onSelectType={handleSelectType} />;
    }
  };

  return (
    <>
      <DeprecationBanner />
      {renderContent()}
    </>
  );
};

export default AgentCreator;